type DateLike =
  | Date
  | string
  | { year: number; month: number; day: number }
  | { year: () => number; month: () => number; date?: () => number; day?: () => number }
  | null
  | undefined;

function pad(value: number) {
  return String(value).padStart(2, '0');
}

function fromObjectDate(value: Exclude<DateLike, Date | string | null | undefined>) {
  if (
    typeof (value as { year: () => number }).year === 'function' &&
    typeof (value as { month: () => number }).month === 'function' &&
    (typeof (value as { date?: () => number }).date === 'function' ||
      typeof (value as { day?: () => number }).day === 'function')
  ) {
    const year = (value as { year: () => number }).year();
    const month = (value as { month: () => number }).month() + 1;
    const day =
      (value as { date?: () => number }).date?.() ?? (value as { day?: () => number }).day?.() ?? 1;

    return { year, month, day };
  }

  if (
    typeof (value as { year: number }).year === 'number' &&
    typeof (value as { month: number }).month === 'number' &&
    typeof (value as { day: number }).day === 'number'
  ) {
    return {
      year: (value as { year: number }).year,
      month: (value as { month: number }).month,
      day: (value as { day: number }).day
    };
  }

  return null;
}

function toLocalDateParts(value: DateLike) {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.valueOf())) return null;
    return {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate()
    };
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
    if (isoMatch) {
      return {
        year: Number(isoMatch[1]),
        month: Number(isoMatch[2]),
        day: Number(isoMatch[3])
      };
    }

    const dotMatch = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(trimmed);
    if (dotMatch) {
      return {
        year: Number(dotMatch[3]),
        month: Number(dotMatch[2]),
        day: Number(dotMatch[1])
      };
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.valueOf())) return null;
    return {
      year: parsed.getFullYear(),
      month: parsed.getMonth() + 1,
      day: parsed.getDate()
    };
  }

  return fromObjectDate(value);
}

export function toLocalDateString(value: DateLike): string | undefined {
  const parts = toLocalDateParts(value);
  if (!parts) return undefined;
  return `${String(parts.year).padStart(4, '0')}-${pad(parts.month)}-${pad(parts.day)}`;
}

export function toLocalDateTimeBoundaryString(value: DateLike, boundary: 'start' | 'end') {
  const date = toLocalDateString(value);
  if (!date) return undefined;
  return boundary === 'start' ? `${date} 00:00:00` : `${date} 23:59:59`;
}

export function normalizeDateRangePayload<
  T extends {
    date_from?: DateLike;
    date_to?: DateLike;
    date_created_from?: DateLike;
    date_created_to?: DateLike;
  }
>(payload: T): T {
  const normalizedPayload = { ...payload };
  const dateFrom = toLocalDateTimeBoundaryString(payload.date_from as DateLike, 'start');
  const dateTo = toLocalDateTimeBoundaryString(payload.date_to as DateLike, 'end');
  const dateCreatedFrom = toLocalDateTimeBoundaryString(
    payload.date_created_from as DateLike,
    'start'
  );
  const dateCreatedTo = toLocalDateTimeBoundaryString(payload.date_created_to as DateLike, 'end');

  if (dateFrom) normalizedPayload.date_from = dateFrom;
  if (dateTo) normalizedPayload.date_to = dateTo;
  if (dateCreatedFrom) normalizedPayload.date_created_from = dateCreatedFrom;
  if (dateCreatedTo) normalizedPayload.date_created_to = dateCreatedTo;

  return normalizedPayload;
}
